/* Invoices + real revenue dashboard (paid Razorpay collections and completed visits). */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { IndianRupee, Search, FileText, ExternalLink } from 'lucide-react';
import { openAuthedFile } from '../staff/staffShared';
import { useSortedPage, SortTh, PageBar } from '../../components/admin/DataTable';

function inr(v) {
  return `₹${Number(v || 0).toLocaleString('en-IN')}`;
}

function openInvoice(bookingId, headers, onError) {
  return openAuthedFile(
    `${config.API}/admin/bookings/${bookingId}/invoice`,
    onError,
    headers,
    { allowHtml: true, filenameFallback: 'invoice' },
  );
}

export default function Invoices() {
  const { getAuthHeaders } = useAuth();
  const [revenue, setRevenue] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('invoices');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const h = { headers: getAuthHeaders() };
      const [rRes, iRes, pRes] = await Promise.all([
        axios.get(`${config.API}/admin/revenue`, h),
        axios.get(`${config.API}/admin/invoices`, h),
        axios.get(`${config.API}/admin/payments`, h),
      ]);
      setRevenue(rRes.data);
      setInvoices(iRes.data.items || []);
      setTotalAmount(iRes.data.total_amount || 0);
      setPayments(pRes.data.items || []);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load invoices');
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const q = search.toLowerCase();
  const filtered = invoices.filter(i =>
    !search ||
    (i.customer_name || '').toLowerCase().includes(q) ||
    (i.customer_phone || '').includes(search) ||
    (i.invoice_no || '').toLowerCase().includes(q) ||
    (i.service_name || '').toLowerCase().includes(q) ||
    (i.booking_id || '').toLowerCase().includes(q) ||
    (i.razorpay_payment_id || '').toLowerCase().includes(q) ||
    (i.razorpay_order_id || '').toLowerCase().includes(q)
  );
  const filteredPays = payments.filter(p =>
    !search ||
    (p.customer_name || '').toLowerCase().includes(q) ||
    (p.razorpay_payment_id || '').toLowerCase().includes(q) ||
    (p.razorpay_order_id || '').toLowerCase().includes(q) ||
    (p.booking_ids || []).join(' ').toLowerCase().includes(q) ||
    (p.service_name || '').toLowerCase().includes(q)
  );
  const invTable = useSortedPage(filtered, { defaultKey: 'paid_at', defaultDir: 'desc' });
  const payTable = useSortedPage(filteredPays, { defaultKey: 'paid_at', defaultDir: 'desc' });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;

  const maxMonthly = Math.max(1, ...(revenue?.monthly || []).map(m => m.revenue));

  return (
    <div data-testid="admin-invoices" className="space-y-6">
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ['Collected today', revenue?.collected_today ?? revenue?.today_revenue],
          ['Collected this month', revenue?.collected_month ?? revenue?.month_revenue],
          ['All-time collected', revenue?.collected_total ?? revenue?.total_revenue],
        ].map(([label, val]) => (
          <div key={label} className="bg-white rounded-xl border border-[#673c96]/10 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#673c96]/70">{label}</p>
            <p className="text-2xl font-bold text-[#311b52] mt-1 flex items-center" style={{ fontFamily: 'Poppins' }}>
              <IndianRupee className="w-5 h-5" />{Number(val || 0).toLocaleString('en-IN')}
            </p>
          </div>
        ))}
        <div className="bg-white rounded-xl border border-[#673c96]/10 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#673c96]/70">Payments</p>
          <p className="text-sm text-[#311b52] mt-1 leading-6">
            <span className="font-bold text-emerald-700">{revenue?.counts?.paid ?? 0}</span> paid bookings ·{' '}
            <span className="font-bold text-[#f37721]">{revenue?.counts?.razorpay_paid ?? 0}</span> Razorpay receipts ·{' '}
            <span className="font-bold text-gray-500">{revenue?.counts?.completed ?? 0}</span> completed visits
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-[#673c96]/10 p-5">
          <h3 className="text-sm font-bold text-[#311b52] mb-4" style={{ fontFamily: 'Poppins' }}>Monthly collections (paid + completed)</h3>
          {(revenue?.monthly || []).length === 0 ? (
            <p className="text-sm text-[#673c96]">No paid or completed bookings yet — collections appear here after the first payment.</p>
          ) : (
            <div className="flex items-end gap-2 h-40">
              {(revenue.monthly || []).map(m => (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: ${inr(m.revenue)} (${m.bookings} bookings)`}>
                  <span className="text-[9px] text-[#673c96] font-semibold">{inr(m.revenue)}</span>
                  <div className="w-full bg-gradient-to-t from-[#673c96] to-[#f37721] rounded-t-md"
                    style={{ height: `${Math.max(6, (m.revenue / maxMonthly) * 110)}px` }} />
                  <span className="text-[9px] text-[#6b7280]">{m.month.slice(5)}/{m.month.slice(2, 4)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-[#673c96]/10 p-5">
          <h3 className="text-sm font-bold text-[#311b52] mb-4" style={{ fontFamily: 'Poppins' }}>Top services by revenue</h3>
          {(revenue?.top_services || []).length === 0 ? (
            <p className="text-sm text-[#673c96]">Nothing yet.</p>
          ) : (
            <div className="space-y-2.5">
              {(revenue.top_services || []).map(s => {
                const max = revenue.top_services[0]?.revenue || 1;
                return (
                  <div key={s.service}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="font-medium text-[#311b52]">{s.service}</span>
                      <span className="text-[#673c96] font-semibold">{inr(s.revenue)}</span>
                    </div>
                    <div className="h-2 bg-[#f4f5f7] rounded-full overflow-hidden">
                      <div className="h-full bg-[#673c96] rounded-full" style={{ width: `${(s.revenue / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {[['invoices', `Invoices (${filtered.length})`], ['payments', `Razorpay payments (${filteredPays.length})`]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setTab(id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === id ? 'bg-[#673c96] text-white' : 'bg-white border border-[#673c96]/20 text-[#673c96]'}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#673c96]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, invoice, payment ID…"
              className="pl-10 pr-4 py-2 rounded-lg border border-[#673c96]/20 text-sm outline-none focus:border-[#673c96]" data-testid="invoice-search" />
          </div>
        </div>

        {tab === 'invoices' ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
            <div className="px-4 py-3 border-b border-[#673c96]/10 text-sm text-[#673c96]">
              Paid total {inr(totalAmount)} — open an invoice to print or save a receipt.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead className="bg-[#fdfcff]">
                  <tr className="border-b border-[#673c96]/10">
                    <SortTh label="Invoice #" k="invoice_no" sortKey={invTable.sortKey} sortDir={invTable.sortDir} onSort={invTable.toggleSort} />
                    <SortTh label="Date" k="paid_at" sortKey={invTable.sortKey} sortDir={invTable.sortDir} onSort={invTable.toggleSort} />
                    <SortTh label="Customer" k="customer_name" sortKey={invTable.sortKey} sortDir={invTable.sortDir} onSort={invTable.toggleSort} />
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">Line items</th>
                    <SortTh label="Total" k="amount" sortKey={invTable.sortKey} sortDir={invTable.sortDir} onSort={invTable.toggleSort} />
                    <SortTh label="Payment" k="payment_status" sortKey={invTable.sortKey} sortDir={invTable.sortDir} onSort={invTable.toggleSort} />
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">Razorpay</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]" />
                  </tr>
                </thead>
                <tbody>
                  {invTable.slice.map(i => (
                    <tr key={i.invoice_no + (i.booking_id || '')} className="border-b border-[#673c96]/5 hover:bg-[#fdfcff]" data-testid={`invoice-${i.invoice_no}`}>
                      <td className="py-3 px-4 font-mono text-xs text-[#673c96]">{i.invoice_no}</td>
                      <td className="py-3 px-4 text-[#673c96]">{i.paid_at ? i.paid_at.slice(0, 16).replace('T', ' ') : i.date}</td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-[#311b52]">{i.customer_name || '—'}</p>
                        <p className="text-xs text-[#673c96]">{i.beneficiary_name}</p>
                      </td>
                      <td className="py-3 px-4 text-xs text-[#311b52]">
                        {(i.lines || []).length ? (i.lines || []).map((l) => (
                          <p key={l.id || l.label}>{l.label}: {inr(l.amount)}</p>
                        )) : <p>{i.service_name}</p>}
                      </td>
                      <td className="py-3 px-4 font-semibold text-[#311b52]">{inr(i.amount)}</td>
                      <td className="py-3 px-4">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          i.payment_status === 'paid' || i.payment_status === 'paid_offline' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
                          {i.payment_status}{i.payment_method && i.payment_method !== '—' ? ` · ${i.payment_method}` : ''}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-[11px] text-[#673c96]">
                        {i.razorpay_payment_id || '—'}
                        {i.razorpay_order_id ? <p className="text-[10px] text-[#9ca3af]">{i.razorpay_order_id}</p> : null}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <button type="button"
                          onClick={() => openInvoice(i.booking_id, getAuthHeaders(), setError)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#673c96] hover:text-[#f37721]">
                          <FileText className="w-3.5 h-3.5" /> Invoice
                        </button>
                        {i.receipt_url ? (
                          <a href={i.receipt_url} target="_blank" rel="noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-[#f37721]">
                            <ExternalLink className="w-3.5 h-3.5" /> Razorpay
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="py-10 text-center text-sm text-[#673c96]">
                      No invoices yet — they appear when a booking is paid or completed.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <PageBar page={invTable.page} pages={invTable.pages} total={invTable.total} setPage={invTable.setPage} noun="invoices" />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead className="bg-[#fdfcff]">
                  <tr className="border-b border-[#673c96]/10">
                    {['Paid at', 'Customer', 'Amount', 'Status', 'Payment ID', 'Order ID', 'Bookings', ''].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payTable.slice.map(p => (
                    <tr key={p.id || p.razorpay_order_id} className="border-b border-[#673c96]/5 hover:bg-[#fdfcff]">
                      <td className="py-3 px-4 text-xs text-[#673c96]">{(p.paid_at || p.created_at || '').slice(0, 16).replace('T', ' ') || '—'}</td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-[#311b52]">{p.customer_name || '—'}</p>
                        <p className="text-xs text-[#673c96]">{p.service_name}</p>
                      </td>
                      <td className="py-3 px-4 font-semibold text-[#311b52]">{inr(p.amount)}</td>
                      <td className="py-3 px-4">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : p.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                          {p.status}{p.provider ? ` · ${p.provider}` : ''}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-[11px] text-[#673c96]">{p.razorpay_payment_id || '—'}</td>
                      <td className="py-3 px-4 font-mono text-[11px] text-[#9ca3af]">{p.razorpay_order_id || '—'}</td>
                      <td className="py-3 px-4 font-mono text-[11px] text-[#673c96]">{(p.booking_ids || []).join(', ') || '—'}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {(p.booking_ids || [])[0] ? (
                          <button type="button"
                            onClick={() => openInvoice(p.booking_ids[0], getAuthHeaders(), setError)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#673c96] hover:text-[#f37721]">
                            <FileText className="w-3.5 h-3.5" /> Invoice
                          </button>
                        ) : null}
                        {p.receipt_url ? (
                          <a href={p.receipt_url} target="_blank" rel="noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-[#f37721]">
                            <ExternalLink className="w-3.5 h-3.5" /> Razorpay
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {filteredPays.length === 0 && (
                    <tr><td colSpan={8} className="py-10 text-center text-sm text-[#673c96]">
                      No Razorpay payment records yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <PageBar page={payTable.page} pages={payTable.pages} total={payTable.total} setPage={payTable.setPage} noun="payments" />
          </div>
        )}
      </div>
    </div>
  );
}
